import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import type { DropdownProps } from './Dropdown.types';

/** Folga entre o gatilho e o menu, nos dois sentidos da abertura. */
const GAP_PX = 4;

export function Dropdown({
  trigger,
  items,
  align = 'left',
  className,
  portal = false,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [fixedStyle, setFixedStyle] = useState<React.CSSProperties | null>(
    null,
  );

  const actionItems = items.filter((item) => !item.divider);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    const noGatilho = containerRef.current?.contains(target);
    // No modo portal o menu NÃO está dentro do container do gatilho, então sem
    // esta segunda checagem o primeiro clique num item fecharia o menu antes de
    // o `onClick` do item rodar.
    const noMenu = menuRef.current?.contains(target);
    if (!noGatilho && !noMenu) {
      setOpen(false);
      setFocusedIndex(-1);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, handleClickOutside]);

  /**
   * Põe o menu sobre o gatilho em coordenadas de viewport.
   *
   * Abre para baixo por padrão e **vira para cima** quando não há altura
   * sobrando — sem isso o menu da última linha de uma tabela comprida abriria
   * fora da tela, e `fixed` não gera scroll para ir buscá-lo.
   */
  const place = useCallback(() => {
    const gatilho = containerRef.current?.getBoundingClientRect();
    if (!gatilho) return;

    const alturaMenu = menuRef.current?.offsetHeight ?? 0;
    const cabeEmbaixo =
      gatilho.bottom + GAP_PX + alturaMenu <= window.innerHeight;

    setFixedStyle({
      position: 'fixed',
      ...(cabeEmbaixo
        ? { top: gatilho.bottom + GAP_PX }
        : { bottom: window.innerHeight - gatilho.top + GAP_PX }),
      ...(align === 'right'
        ? { right: window.innerWidth - gatilho.right }
        : { left: gatilho.left }),
    });
  }, [align]);

  // Duas passadas de propósito: a primeira monta o menu para ele ter altura,
  // a segunda decide o lado com a altura medida. `useLayoutEffect` faz isso
  // antes da pintura, então não se vê o menu pular.
  useLayoutEffect(() => {
    if (!portal || !open) {
      setFixedStyle(null);
      return;
    }
    place();
  }, [portal, open, place, items.length]);

  useEffect(() => {
    if (!portal || !open) return;
    // `capture` para ouvir o scroll de qualquer ancestral (o `overflow-auto` da
    // Table inclusive), não só o da janela.
    const recolocar = () => place();
    window.addEventListener('scroll', recolocar, true);
    window.addEventListener('resize', recolocar);
    return () => {
      window.removeEventListener('scroll', recolocar, true);
      window.removeEventListener('resize', recolocar);
    };
  }, [portal, open, place]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen(true);
          setFocusedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setFocusedIndex((prev) => {
            let next = (prev + 1) % actionItems.length;
            while (
              'disabled' in actionItems[next] &&
              actionItems[next].disabled
            ) {
              next = (next + 1) % actionItems.length;
            }
            return next;
          });
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setFocusedIndex((prev) => {
            let next = (prev - 1 + actionItems.length) % actionItems.length;
            while (
              'disabled' in actionItems[next] &&
              actionItems[next].disabled
            ) {
              next = (next - 1 + actionItems.length) % actionItems.length;
            }
            return next;
          });
          break;
        }
        case 'Escape': {
          e.preventDefault();
          setOpen(false);
          setFocusedIndex(-1);
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const item = actionItems[focusedIndex];
          if (item && !item.divider && !item.disabled && item.onClick) {
            item.onClick();
            setOpen(false);
            setFocusedIndex(-1);
          }
          break;
        }
        case 'Home': {
          e.preventDefault();
          setFocusedIndex(0);
          break;
        }
        case 'End': {
          e.preventDefault();
          setFocusedIndex(actionItems.length - 1);
          break;
        }
      }
    },
    [open, actionItems, focusedIndex],
  );

  let actionIndex = -1;

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      style={
        portal ? (fixedStyle ?? { position: 'fixed', opacity: 0 }) : undefined
      }
      className={cn(
        'z-50 min-w-[180px] rounded-md border border-neutral-200 bg-white py-1 shadow-lg',
        'dark:border-neutral-700 dark:bg-neutral-900',
        // No portal a posição vem do `style` (coordenadas de viewport); fora
        // dele continua sendo o `absolute` de sempre, relativo ao gatilho.
        portal ? 'fixed' : 'absolute mt-1',
        !portal && (align === 'right' ? 'right-0' : 'left-0'),
      )}
      onKeyDown={handleKeyDown}
    >
      {items.map((entry) => {
        if (entry.divider) {
          return (
            <div
              key={entry.id}
              className="my-1 h-px bg-neutral-200 dark:bg-neutral-700"
              role="separator"
            />
          );
        }

        actionIndex++;
        const isFocused = actionIndex === focusedIndex;

        return (
          <button
            key={entry.id}
            type="button"
            role="menuitem"
            tabIndex={-1}
            disabled={entry.disabled}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
              'transition-colors duration-100',
              isFocused && 'bg-neutral-100 dark:bg-neutral-800',
              entry.disabled
                ? 'cursor-not-allowed text-neutral-400 dark:text-neutral-600'
                : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800',
            )}
            onClick={() => {
              if (!entry.disabled) {
                entry.onClick?.();
                setOpen(false);
                setFocusedIndex(-1);
              }
            }}
          >
            {entry.icon && <span aria-hidden="true">{entry.icon}</span>}
            {entry.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <div
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          setOpen((prev) => !prev);
          if (!open) setFocusedIndex(0);
        }}
        onKeyDown={handleKeyDown}
      >
        {trigger}
      </div>
      {open &&
        (portal && typeof document !== 'undefined'
          ? createPortal(menu, document.body)
          : menu)}
    </div>
  );
}
